// Make the watchdogs audible in development.
//
// THE GAP THIS CLOSES: four watchdogs cover never-settling work —
//   `lib/database/logger/slow-query-report` (1s, per SQL statement)
//   `lib/async-watchdog/watch-async-step`   (3s, wrapped async steps)
//   `lib/sentry/stuck-query-watchdog`       (8s, every TanStack query)
//   `lib/database/engine/report-stuck-connection-lock` (5s, actor lock holder)
// All four are pure OBSERVATION by design: they never time out and never reject, because aborting
// would mask the bug they exist to reveal. That is the right call — but it means their only product
// is a report, and every one of them reports solely through `captureSentryException`, which
// `sentry-setup.native.tsx` gates `enabled: !__DEV__` (and `sentry-setup.web.tsx` gates further on
// the reporting variant). So in local development all four fire into a void: a query that hangs
// forever produces a spinner and total silence.
//
// That silence is what let a stale-app hang (an app still running after `vpx _dev` recreated the
// actor sync DB, holding a cursor for a generation the server no longer has) look like "the app is
// just slow" instead of "four watchdogs are screaming". The reports were correct and timely; nobody
// could hear them.
//
// WHAT THIS DOES: mirrors watchdog-shaped captures to the console in dev only. On native that
// reaches the Metro pane, and therefore `.mprocs-logs/han (:<port>).log`, which is where a dev (or
// an agent) actually looks. Production behaviour is untouched — this is inert unless `__DEV__`.
//
// WHY IT LIVES HERE AND TAKES NO IMPORTS FROM `sentry-setup`: same constraint the stuck-query
// watchdog documents — `captureSentryException` lives in the platform-specific `sentry-setup.*`
// files, and importing them back would close a cycle. This module is a leaf: it imports nothing
// from the app, so `sentry-setup.*` can call it freely.
//
// WHY NOT THE APP LOGGER: `lib/logger`'s app LogLayer installs a Sentry transport
// (`app-log-layer.native.ts`), so routing watchdog reports through it would risk both an import
// cycle and a report→log→report loop. Console references are captured at module load, the same
// technique `lib/logger/original-console` uses, so a later `globalThis.console` reassignment (or a
// logger that wraps console) cannot redirect or recurse through this path.

/**
 * Console info, bound at module load. Captured rather than called through `globalThis.console` so
 * this sink cannot be re-entered by whatever later wraps the console. These reports observe work
 * that is still running, not a rejected operation: console.error would create a native LogBox
 * banner that blocks controls even after the work completes. Enforced deadlines report failures
 * separately; watchdog diagnostics remain visible in Metro without changing the app's UI.
 */
const CONSOLE_INFO: (...args: unknown[]) => void = Function.prototype.bind.call(
  globalThis.console.info,
  globalThis.console,
);

/**
 * `Error.name` values the watchdogs stamp on their reports. Only these are mirrored: everything
 * else reaching `captureSentryException` is an ordinary caught error that already surfaced through
 * an error boundary, a rejected promise, or a red box — mirroring those would be noise.
 */
const WATCHDOG_ERROR_NAMES = new Set([
  "SlowQueryWatchdog",
  "StuckConnectionLockWatchdog",
  "StuckQueryWatchdog",
  "StuckStepWatchdog",
]);

/** The subset of an `Error` this sink reads. Keeps the check total for non-Error captures. */
interface NamedError {
  message?: unknown;
  name?: unknown;
}

function readNamedError(exception: unknown): NamedError | null {
  return typeof exception === "object" && exception !== null ? (exception as NamedError) : null;
}

/**
 * Whether `exception` is one of the watchdog reports this sink exists to surface.
 *
 * Exported for tests: the routing decision is the whole behaviour, so it is asserted directly
 * rather than through the console side effect.
 */
export function isWatchdogReport(exception: unknown): boolean {
  const named = readNamedError(exception);
  return named !== null && typeof named.name === "string" && WATCHDOG_ERROR_NAMES.has(named.name);
}

/**
 * Mirror a watchdog report to the console when running in development.
 *
 * Called from `captureSentryException` on every platform — the single chokepoint every watchdog
 * already funnels through, so one call site covers all four. A no-op for non-watchdog exceptions
 * and outside `__DEV__`; never throws, since instrumentation must not become a failure of its own.
 */
export function mirrorWatchdogReportToDevConsole(exception: unknown): void {
  if (!__DEV__ || !isWatchdogReport(exception)) {
    return;
  }
  try {
    const named = readNamedError(exception);
    const name = typeof named?.name === "string" ? named.name : "Watchdog";
    const message = typeof named?.message === "string" ? named.message : String(exception);
    // One line, prefixed so it greps out of a busy Metro pane. The report objects carry their
    // detail on the Sentry `contexts`, which is not reconstructable here; the message already
    // names the elapsed time and the subject, which is what makes the hang identifiable.
    CONSOLE_INFO(`[watchdog] ${name}: ${message}`);
  } catch {
    // A diagnostic must never surface as an application failure.
  }
}
